import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	NewsletterData,
	OpOostInformationFieldsFragment,
	PublicNewsletterFieldsFragment
} from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import IntroHeader from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { getServerPageGetManyNewsletters, getServerPageGetPublicNewsletterRegioExpres } from 'api/graphql-helper';

const Newsletter = dynamic(() => import('modules/newsletter/newsletter'), { ssr: false });

interface INewsletterPublicPageProps extends IBasePageProps {
	condoleanceInformation?: {
		data: {
			attributes: OpOostInformationFieldsFragment;
		};
	};
	newsletterPageFields?: PublicNewsletterFieldsFragment[];
	newsletters?: NewsletterData[];
}

const NewsletterPublicPage = (props: INewsletterPublicPageProps): React.JSX.Element => {
	const page = props.newsletterPageFields?.[0].page;
	const { header, seo } = { ...page };

	const headerDefaultOptions = {
		backgroundColor: header?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout
			typeName={page?.__typename}
			navbar={props?.condoleanceInformation?.data?.attributes?.navbar}
			footer={props?.condoleanceInformation?.data?.attributes?.footer}
		>
			<Head seoModule={seo} seoParent={props?.condoleanceInformation?.data?.attributes?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader
					module={{
						title: header?.title || '',
						backgroundColor: header?.backgroundColor,
						excerpt: header?.excerpt || ''
					}}
					slug={ROUTES.NEWSLETTER_PUBLIC.fullPath}
				/>
			</BaseModule>
			<BaseModule defaultModuleOptions={page?.newsletter?.defaultModuleOptions}>
				<Newsletter module={page?.newsletter} newslettersData={props.newsletters} />
			</BaseModule>
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const newsletterCampaigns = await getServerPageGetManyNewsletters({}, client);

		const result = await getServerPageGetPublicNewsletterRegioExpres(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { regioExpresInformation, publicNewsletterPage } = result.props.data;

		const { getManyNewsletters } = newsletterCampaigns.props.data;

		if (!getManyNewsletters) {
			return {
				notFound: true,
				props: {
					initialReduxState: {
						settings: {
							...settingsInitialState,
							publicationState,
							draftMode
						}
					}
				}
			};
		}

		return {
			props: {
				regioExpresInformation,
				newsletterPageFields: publicNewsletterPage,
				newsletters: getManyNewsletters || [],
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'newsletter-public'
					}
				}
			}
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in nieuwsbrief', error);

		return {
			notFound: true
		};
	}
};

export default NewsletterPublicPage;
